import React, { useCallback } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useDispatch } from 'react-redux';
import { useWindowDimensions } from 'react-native';
import ExpandableGrid from './ExpandableGrid';
import { fetchAlertSummaryDataByProperty } from '../../actions/alertSummaryByProperty';
import { parseFilters } from '../../../Filters/utils/fetchData';
import { transformAlertMetricsByProperty } from '../../../../utils/transformChartsAndTableData';
import { ScrollView } from '../../../../components/Common/react-native-components';

const AlertSummaryTable = ({
  data,
  applied_filter_values,
  columns,
  sortConfig,
  handleOnSortClick,
  sortingInProgress
}) => {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const dispatch = useDispatch();
  const styles = getStyles(isMobile)

  const dataCols = columns?.filter(col => col.field !== 'alert_type') || [];
  const data_cols_width = dataCols.reduce((s, c) => s + (c.width || 100), 0);
  const total_width = columns?.reduce((s, c) => s + (c.width || 100), 0) || 0;
  const percentage_data_cols = (data_cols_width / total_width) * 100;
  const summary_width = percentage_data_cols / ((columns?.length || 0) - 1);

  const {
    total_new = 0,
    total_in_progress = 0,
    total_overdue = 0,
    total_closed = 0,
    total_total = 0,
    alerts = []
  } = data || {};

  const SummaryBlock = ({ label, count, color }) => {
    const containerStyle = {
      width: isMobile ? 'auto' : `${summary_width}%`,
      alignItems: isMobile ? 'center' : 'flex-end',
      borderTopWidth: 4,
      borderTopColor: color,
      backgroundColor: '#FFFFFF',
      marginBottom: 4,
      paddingHorizontal: isMobile ? 4 : 2
    };

    const countTextStyle = {
      fontSize: isMobile ? 14 : 26,
      fontWeight: '700',
      textAlign: 'center'
    };

    return (
      <View style={containerStyle}>
        {isMobile && (
          <View style={{ marginBottom: 10, alignItems: 'center' }}>
            <Text style={{ textAlign: 'center' }}>{label}</Text>
          </View>
        )}
        <Text style={countTextStyle}>{count}</Text>
      </View>
    );
  };

  const summaryMetrics = [
    { label: 'New', count: total_new, color: '#F7C948' },
    { label: 'In Progress', count: total_in_progress, color: '#1A73E8' },
    { label: 'Overdue', count: total_overdue, color: '#D93025' },
    { label: 'Closed', count: total_closed, color: '#A0A0A0' },
    { label: 'Total', count: total_total, color: '#000000' }
  ];

  const tableData = alerts.map((alert, index) => ({
    id: `${index}`,
    alert_type: alert?.alert_type || alert?.property,
    new_count: alert?.new ?? 0,
    in_progress: alert?.in_progress ?? 0,
    overdue: alert?.overdue ?? 0,
    closed: alert?.closed ?? 0,
    total_count: alert?.total ?? 0,
    subData: [{}]
  }));

  const getNestedData = useCallback(
    async row => {
      await new Promise(res => setTimeout(res, 1000));

      const { filters } = parseFilters(applied_filter_values);

      const updatedFilters = {
        ...filters,
        additional_filters: {
          ...(filters?.additional_filters || {}),
          alert_type: [row?.alert_type]
        }
      };

      const rawData = await dispatch(fetchAlertSummaryDataByProperty(updatedFilters));
      const transformed = transformAlertMetricsByProperty(rawData);

      return (
        transformed?.alerts?.map((item, idx) => ({
          id: `nested_${idx}_${item?.property}`,
          alert_type: item?.property,
          new_count: item?.new  || 0,
          in_progress: item?.in_progress || 0,
          overdue: item?.overdue || 0,
          closed: item?.closed || 0,
          total_count: item?.total || 0
        })) || [{}]
      );
    },
    [applied_filter_values, dispatch]
  );

  return (
    <View style={{ marginTop: isMobile ? 20 : 10, marginBottom: 30, marginHorizontal: isMobile ? 4 : 10 }}>
      <ScrollView horizontal contentContainerStyle={styles.headerContainer} showsHorizontalScrollIndicator={true}>
        {summaryMetrics.map((item, i) => (
          <SummaryBlock key={i} {...item} />
        ))}
      </ScrollView>

      <View style={{ marginTop: 15 }}>
        <ExpandableGrid
          columns={columns}
          data={tableData}
          getNestedData={getNestedData}
          sortConfig={sortConfig}
          handleOnSortClick={handleOnSortClick}
          sortingInProgress={sortingInProgress}
        />
      </View>
    </View>
  );
};

export default AlertSummaryTable;

const getStyles = isMobile =>
  StyleSheet.create({
    headerContainer: {
      flexDirection: 'row',
      backgroundColor: '#FFFFFF',
      borderColor: '#E5E7EB',
      justifyContent: isMobile ? 'center' : 'flex-end',
      flexWrap: 'nowrap',
      gap: isMobile ? 4 : 0,
      overflow: 'hidden',
      flexGrow: 1
    }
  });
